import React from 'react';

export interface InputProps {
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search' | 'file';
  name?: string;
  id?: string;
  value?: string | number;
  defaultValue?: string | number;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  maxLength?: number;
  min?: string | number;
  max?: string | number;
  step?: string | number;
  readOnly?: boolean;
  fullWidth?: boolean;
  size?: 'sm' | 'md' | 'lg';
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  autoComplete?: string;
  accept?: string;
  multiple?: boolean;
  className?: string;
}

export const Input: React.FC<InputProps> = ({
  type = 'text',
  name,
  id,
  value,
  defaultValue,
  placeholder,
  disabled = false,
  required = false,
  error = false,
  fullWidth = false,
  size = 'md',
  onChange,
  onBlur,
  onFocus,
  onKeyDown,
  autoComplete,
  className = '',
  maxLength,
  min,
  max,
  step,
  readOnly = false,
  accept,
  multiple,
}) => {
  const baseStyles =
    'border rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:bg-gray-100 disabled:cursor-not-allowed';

  const sizeStyles = {
    sm: 'px-2 py-1 text-sm',
    md: 'px-3 py-2 text-base',
    lg: 'px-4 py-3 text-lg',
  };

  const stateStyles = error
    ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500';

  const widthStyle = fullWidth ? 'w-full' : '';
  const inputClasses = `${baseStyles} ${sizeStyles[size]} ${stateStyles} ${widthStyle} ${className}`;

  return (
    <input
      type={type}
      name={name}
      id={id}
      value={value}
      defaultValue={defaultValue}
      placeholder={placeholder}
      disabled={disabled}
      required={required}
      readOnly={readOnly}
      onChange={onChange}
      onBlur={onBlur}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      autoComplete={autoComplete}
      maxLength={maxLength}
      min={min}
      max={max}
      step={step}
      accept={accept}
      multiple={multiple}
      className={inputClasses}
      aria-invalid={error}
    />
  );
};

export default Input;
